import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Spinner } from '@/components/Button';
import ChannelsFilter from '@/components/ChannelsFilter';
import { Controls, ControlsButton } from '@/components/Controls';
import FullScreenButton from '@/components/FullScreenButton';
import InfiniteLoader from '@/components/InfiniteLoader';
import {
  PageHeading,
  SectionHeading,
  TeamHeading,
} from '@/components/Layout/Parts';
import { TableHeader } from '@/components/Table';
import Text from '@/components/Text';
import TopicsFilter from '@/components/TopicsFilter';
import useChannelsFilter from '@/hooks/useChannelsFilter';
import useDidUpdate from '@/hooks/useDidUpdate';
import { selectTeamSettings } from '@/selectors';
import {
  PaginatedResponse,
  TopVideosResource,
  VideoTopicResource,
} from '@/types/resources';
import { TeamSettings } from '@/types/resources';
import { api } from '@/utils/api';
import { RootState } from '@/utils/store';

import TopThumbnailsCard from './components/TopThumbnailsCard';
import TopThumbnailsFilter from './components/TopThumbnailsFilter';
import TopThumbnailsSort, { Sort } from './components/TopThumbnailsSort';

enum Tabs {
  Competitors = 1,
  Owned = 0,
  All = -1,
}

const DEFAULT_SORT_STATE = Sort.TOTAL_VIEWS;

const ThumbnailsGrid = styled.div`
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  padding: 24px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-gap: 16px 24px;
`;

const SpinnerHolder = styled.div`
  text-align: center;
  grid-column: 1/4;
  margin: 4px 0 3px;
`;

const LoaderText = styled(Text)`
  display: inline-block;
`;

const StyledSpinner = styled(Spinner)`
  width: 18px;
  height: 18px;
  margin-bottom: -5px;
`;

const FilterWrap = styled.div`
  margin: 24px 0;
  display: flex;
  align-items: flex-end;
`;

const ErrorText = styled(Text)`
  grid-column: 1/4;
  text-align: center;
  margin: 4px 0;
`;

const ButtonsHolder = styled.div`
  display: flex;
  gap: 8px;
`;

const TopThumbnails = () => {
  const [activeTab, setActiveTab] = useState(Tabs.Competitors);

  const [topics, setTopics] = useState<VideoTopicResource[]>([]);

  const [activeSort, setActiveSort] = useState(DEFAULT_SORT_STATE);

  const { teamId } = useParams();

  const organisationName = useSelector(
    (rootState: RootState) => rootState.auth.user!.organisation!.name,
  );

  const teamSettings = useSelector((rootState: RootState) =>
    selectTeamSettings(rootState, teamId!),
  );

  const [filteredChannelsIds, filteredChannelsKey] = useChannelsFilter();

  const renderTabHeading = (tab: Tabs) => {
    switch (tab) {
      case Tabs.Competitors:
        return 'Competitors';
      case Tabs.Owned:
        return organisationName;
      default:
        return 'All';
    }
  };

  const fetchThumbnails = ({
    pageParam = `/v1/teams/${teamId}/videos/top`,
  }) => {
    return api.get<never, PaginatedResponse<TopVideosResource>>(pageParam, {
      params: {
        'filter[was_live]': 0,
        'filter[unreachable]': 0,
        ...(activeTab !== Tabs.All && {
          'filter[competitor]': activeTab,
        }),
        ...(filteredChannelsIds?.length && {
          'filter[channel_id]': filteredChannelsIds.join(','),
        }),
        ...(!!topics.length && {
          'filter[topic]': topics.map((topic) => topic.id).join(','),
          topic_operator: 'OR',
        }),
        ...(!!teamSettings?.[
          'top_thumbnails_min_views' as keyof TeamSettings
        ] && {
          'filter[views]':
            teamSettings[`top_thumbnails_min_views` as keyof TeamSettings],
        }),
        ...(!!teamSettings?.[
          'top_thumbnails_min_duration' as keyof TeamSettings
        ] && {
          'filter[min_duration]':
            teamSettings[`top_thumbnails_min_duration` as keyof TeamSettings],
        }),
        ...(!!teamSettings?.[
          'top_thumbnails_min_age' as keyof TeamSettings
        ] && {
          'filter[age]':
            teamSettings[`top_thumbnails_min_age` as keyof TeamSettings],
        }),
        ...(!!teamSettings?.[
          'top_thumbnails_min_v7_index' as keyof TeamSettings
        ] && {
          'filter[v7_index]':
            teamSettings[`top_thumbnails_min_v7_index` as keyof TeamSettings],
        }),
        sort: `-${activeSort}`,
        withOriginalThumbnail: 1,
      },
    });
  };

  const {
    data,
    isError,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    refetch,
    remove,
    fetchNextPage,
  } = useInfiniteQuery<PaginatedResponse<TopVideosResource>>(
    ['top_thumbnails', { teamId }],
    fetchThumbnails,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
    },
  );

  const thumbnails = useMemo(
    () =>
      Object.keys(data?.pages || {}).reduce<TopVideosResource[]>(
        (acc, currKey: any) => {
          return [...acc, ...data!.pages[currKey].data];
        },
        [],
      ),
    [data],
  );

  useDidUpdate(() => {
    remove();
    refetch();
  }, [filteredChannelsKey, activeTab, topics, activeSort, teamSettings]);

  return (
    <>
      <TeamHeading />
      <PageHeading>Top Thumbnails</PageHeading>
      <TableHeader>
        <Controls>
          <ControlsButton
            active={activeTab === Tabs.Owned}
            onClick={() => setActiveTab(Tabs.Owned)}
          >
            {renderTabHeading(Tabs.Owned)}
          </ControlsButton>
          <ControlsButton
            active={activeTab === Tabs.Competitors}
            onClick={() => setActiveTab(Tabs.Competitors)}
          >
            {renderTabHeading(Tabs.Competitors)}
          </ControlsButton>
          <ControlsButton
            active={activeTab === Tabs.All}
            onClick={() => setActiveTab(Tabs.All)}
          >
            {renderTabHeading(Tabs.All)}
          </ControlsButton>
        </Controls>
        <ButtonsHolder>
          <TopicsFilter topics={topics} setTopics={setTopics} />
          <FullScreenButton />
        </ButtonsHolder>
      </TableHeader>
      <ChannelsFilter />
      <FilterWrap>
        <TopThumbnailsFilter />
        <TopThumbnailsSort
          activeSort={activeSort}
          setActiveSort={setActiveSort}
        />
      </FilterWrap>
      <SectionHeading>{renderTabHeading(activeTab)} thumbnails</SectionHeading>
      <ThumbnailsGrid>
        {isLoading ? (
          <SpinnerHolder>
            <StyledSpinner />
            <LoaderText $type="smallMedium">Loading data...</LoaderText>
          </SpinnerHolder>
        ) : isError ? (
          <ErrorText $type="smallMedium">An error occurred!</ErrorText>
        ) : !!!thumbnails.length ? (
          <ErrorText $type="smallMedium">No data</ErrorText>
        ) : (
          thumbnails.map((thumbnail) => (
            <TopThumbnailsCard
              activeSort={activeSort}
              thumbnail={thumbnail}
              key={thumbnail.id}
            />
          ))
        )}
      </ThumbnailsGrid>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    </>
  );
};

export default TopThumbnails;
